import { useEffect, useState } from 'react'

export type Theme = 'daylight' | 'dusk'

/** localStorage key — also read by main.tsx for the pre-mount theme bootstrap. */
export const THEME_STORAGE_KEY = 'compass.web.theme'

/** Safe getter — Safari Private Browsing and locked-down iframes throw on access. */
export function readStoredTheme(): Theme | null {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY)
    return stored === 'daylight' || stored === 'dusk' ? stored : null
  } catch {
    return null
  }
}

function preferredTheme(): Theme {
  if (typeof window === 'undefined') return 'daylight'
  const stored = readStoredTheme()
  if (stored) return stored
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dusk' : 'daylight'
}

function apply(theme: Theme) {
  document.documentElement.dataset.theme = theme
  document
    .querySelector('meta[name="theme-color"]:not([media])')
    ?.setAttribute('content', theme === 'dusk' ? '#1A1614' : '#F7F0E4')
}

export function useTheme(): { theme: Theme; setTheme: (t: Theme) => void; toggle: () => void } {
  const [theme, setThemeState] = useState<Theme>(() => preferredTheme())

  useEffect(() => {
    apply(theme)
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme)
    } catch {
      /* localStorage may be disabled (private mode) or full — fall back to in-memory */
    }
  }, [theme])

  const setTheme = (next: Theme) => setThemeState(next)
  const toggle = () => setThemeState((t) => (t === 'daylight' ? 'dusk' : 'daylight'))

  return { theme, setTheme, toggle }
}
